import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import {
  useBrandRulesQuery,
  useUpdateBrandRulesMutation,
} from '../../services/brandRulesService/__generated__/queries.generated';
import { Button } from '../../components/button';
import SwitchComponent from '../../components/switch';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Field,
  FormGrid,
  Input,
  LoadingBlock,
  Select,
  Textarea,
} from '../../components/ui';
import { toastActions } from '../../../store/toastStore';
import {
  carSettingsFor,
  CarSettingDef,
  CarSettingsErrors,
  CarSettingsForm,
  carSettingsPatch,
  deliveryConfirmation,
  describeCarSetting,
  errorsFromServer,
  FieldDef,
  toCarSettingsForm,
} from './carSettingsAdmin';
import { bookingRulesPatch, BookingRulesForm, toBookingRulesForm } from './bookingRulesAdmin';
import { SettingsSection } from '../../components/SectionNav';
import { regionFormOf } from './regionAdmin';
import type { DistanceUnit } from '../../utils/units';

const SettingRow = styled.div`
  ${tw`py-4`}
  & + & {
    border-top: 1px solid var(--color-border);
  }
`;
const SettingHead = styled.div`
  ${tw`flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between`}
`;
const SettingTitle = styled.div`
  ${tw`text-sm font-medium`}
  color: var(--color-text-primary);
`;
const SettingHint = styled.div`
  ${tw`text-xs mt-0.5`}
  color: var(--color-text-secondary);
`;
const ModeSelect = styled.div`
  ${tw`w-full sm:w-64 shrink-0`}
`;
const Fields = styled.div`
  ${tw`mt-3`}
`;
const ToggleRow = styled.label`
  ${tw`flex items-start justify-between gap-4 py-3 cursor-pointer`}
`;

function codeOf(error: unknown): string | undefined {
  return (error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> })
    ?.graphQLErrors?.[0]?.extensions?.code;
}

function detailsErrorsOf(error: unknown): Array<{ path?: string; message?: string }> | undefined {
  return (
    error as {
      graphQLErrors?: Array<{ extensions?: { details?: { errors?: Array<{ path?: string }> } } }>;
    }
  )?.graphQLErrors?.[0]?.extensions?.details?.errors as
    | Array<{ path?: string; message?: string }>
    | undefined;
}

function SettingFields({
  def,
  form,
  errors,
  onChange,
}: {
  def: CarSettingDef;
  form: CarSettingsForm;
  errors: Record<string, string> | undefined;
  onChange: (key: string, value: string) => void;
}) {
  const mode = def.brandModes.find(item => item.mode === form[def.rule].mode);
  if (!mode || mode.fields.length === 0) return null;
  const input = (field: FieldDef) => {
    const id = `car-setting-${def.rule}-${field.key}`;
    const value = form[def.rule].values[field.key] ?? '';
    if (field.kind === 'boolean') {
      return (
        <Select id={id} value={value} onChange={e => onChange(field.key, e.target.value)}>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </Select>
      );
    }
    if (field.kind === 'text') {
      return (
        <Textarea
          id={id}
          rows={3}
          maxLength={field.max}
          value={value}
          onChange={e => onChange(field.key, e.target.value)}
        />
      );
    }
    return (
      <Input
        id={id}
        inputMode="decimal"
        value={value}
        placeholder={field.nullable ? field.blankMeans : undefined}
        onChange={e => onChange(field.key, e.target.value)}
        data-testid={id}
      />
    );
  };
  return (
    <Fields>
      <FormGrid $cols={mode.fields.length > 1 ? 2 : undefined}>
        {mode.fields.map(field => (
          <Field
            key={field.key}
            label={`${field.label}${field.suffix ? ` (${field.suffix})` : ''}`}
            htmlFor={`car-setting-${def.rule}-${field.key}`}
            hint={field.nullable ? `Leave blank for ${field.blankMeans}.` : undefined}
            error={errors?.[field.key]}
          >
            {input(field)}
          </Field>
        ))}
      </FormGrid>
    </Fields>
  );
}

/**
 * Spec 014b US-3, US-4, US-5: on `/admin/brand-settings`.
 *
 * - "Car settings": each car page setting is set by the hosts, or by the brand
 *   with a value. Without the value the setting cannot be taken (FR-012).
 *   Hosts on this site stop seeing that section; trips here use the brand
 *   value; the cars keep their own values for other brands. Delivery (spec
 *   014c) is set by host or not offered, and a change asks first.
 * - "Booking rules": the minimum driver age, one daily rate, and how deposits
 *   are taken and released.
 *
 * Changes apply to trips booked after saving.
 */
export function CarSettingsSection({
  brandName,
  carSettingsId,
  bookingRulesId,
}: {
  brandName: string;
  /** Spec 030 anchors, so the page nav can jump to each card. */
  carSettingsId?: string;
  bookingRulesId?: string;
}) {
  const { data, loading, error, refetch } = useBrandRulesQuery({ fetchPolicy: 'network-only' });
  const [updateBrandRules, { loading: saving }] = useUpdateBrandRulesMutation({
    refetchQueries: ['BrandRulesHistory', 'HostCapabilities', 'MyBrandRules'],
  });
  const rules = data?.brandRules?.bookingRules as Record<string, unknown> | undefined;
  // Spec 028: the brand's distance rules are in its unit and currency.
  const brandLocale = useMemo(() => {
    const region = regionFormOf(rules);
    return {
      distanceUnit: (region.measurementSystem === 'metric' ? 'km' : 'mi') as DistanceUnit,
      currency: region.currency,
    };
  }, [rules]);
  const carSettings = useMemo(
    () => carSettingsFor(brandLocale.distanceUnit),
    [brandLocale.distanceUnit],
  );

  const loadedForm = useMemo(() => toCarSettingsForm(rules), [rules]);
  const [form, setForm] = useState<CarSettingsForm>(loadedForm);
  const [serverErrors, setServerErrors] = useState<CarSettingsErrors>({});
  useEffect(() => {
    setForm(loadedForm);
    setServerErrors({});
  }, [loadedForm]);

  const loadedRules = useMemo(() => toBookingRulesForm(rules), [rules]);
  const [rulesForm, setRulesForm] = useState<BookingRulesForm>(loadedRules);
  useEffect(() => setRulesForm(loadedRules), [loadedRules]);

  const { patch, errors } = carSettingsPatch(rules, form);
  const settingsDirty = Object.keys(patch).length > 0;
  const settingsValid = Object.keys(errors).length === 0;
  const rulesResult = bookingRulesPatch(rules, rulesForm);
  const rulesDirty = Object.keys(rulesResult.patch).length > 0;

  const setMode = (def: CarSettingDef, mode: string) =>
    setForm(current => ({ ...current, [def.rule]: { ...current[def.rule], mode } }));
  const setValue = (def: CarSettingDef, key: string, value: string) =>
    setForm(current => ({
      ...current,
      [def.rule]: { ...current[def.rule], values: { ...current[def.rule].values, [key]: value } },
    }));

  const save = async (bookingRules: Record<string, unknown>, what: string) => {
    if (!data?.brandRules) return;
    try {
      await updateBrandRules({
        variables: { input: { version: data.brandRules.version, bookingRules } },
      });
      toast.success(`Saved ${what}. New trips use them within about a minute.`);
      await refetch();
    } catch (e) {
      if (codeOf(e) === 'BRAND_RULES_STALE') {
        toast.error(
          'Someone else changed these rules. The latest values are loaded; check them and save again.',
        );
        await refetch();
        return;
      }
      if (codeOf(e) === 'BRAND_RULES_INVALID') {
        setServerErrors(errorsFromServer(detailsErrorsOf(e)));
      }
      toast.error((e as Error).message || `Could not save ${what}`);
    }
  };

  // Spec 014c: turning delivery off or back on asks first; Cancel saves nothing.
  const saveCarSettings = () => {
    const confirmation = deliveryConfirmation(rules, patch, {
      hosts: data?.brandRules?.deliveryHostCount ?? 0,
      trips: data?.brandRules?.openDeliveryTripCount ?? 0,
    });
    if (!confirmation) {
      void save(patch, 'car settings');
      return;
    }
    toastActions.showToastAlert(
      confirmation.title,
      confirmation.message,
      '',
      () => void save(patch, 'car settings'),
      () => undefined,
      confirmation.confirmText,
      'Cancel',
    );
  };

  const shownErrors = (rule: CarSettingDef['rule']) => ({
    ...(serverErrors[rule] ?? {}),
    ...(errors[rule] ?? {}),
  });

  const body = (content: React.ReactNode, label: string) => (
    <>
      {loading && <LoadingBlock label={`Loading ${label}`} />}
      {error && (
        <CardBody>
          <Alert tone="error" title={`Could not load ${label}`}>
            {error.message}
          </Alert>
        </CardBody>
      )}
      {!loading && !error && <CardBody>{content}</CardBody>}
    </>
  );

  return (
    <>
      <SettingsSection id={carSettingsId} label="Car settings">
        <Card>
          <CardHeader
            title="Car settings"
            description={`For each setting on the host car page: hosts set it, or ${brandName} sets one value for every car. When ${brandName} sets it, hosts here no longer see that section, trips on ${brandName} use your value, and the cars keep their own values for other brands. You need a value to take a setting from hosts.`}
          />
          {body(
            carSettings.map(def => {
              const current = form[def.rule];
              const brandSet = current.mode !== def.hostMode;
              const rowErrors = shownErrors(def.rule);
              const loadedSummary = describeCarSetting(
                def.rule,
                rules?.[def.rule],
                brandLocale.distanceUnit,
                brandLocale.currency,
              );
              return (
                <SettingRow key={def.rule} data-testid={`car-setting-${def.rule}`}>
                  <SettingHead>
                    <div>
                      <SettingTitle>{def.title}</SettingTitle>
                      <SettingHint>
                        {brandSet
                          ? `Hosts here stop seeing ${def.hides}.`
                          : loadedSummary
                            ? `Now: ${loadedSummary}`
                            : 'Hosts set it on each car.'}
                      </SettingHint>
                    </div>
                    <ModeSelect>
                      <Select
                        aria-label={`${def.title}: who sets it`}
                        value={current.mode}
                        onChange={e => setMode(def, e.target.value)}
                        data-testid={`car-setting-${def.rule}-mode`}
                      >
                        <option value={def.hostMode}>Set by host</option>
                        {def.brandModes.map(mode => (
                          <option key={mode.mode} value={mode.mode}>
                            {`Set by ${brandName}: ${mode.label}`}
                          </option>
                        ))}
                      </Select>
                    </ModeSelect>
                  </SettingHead>
                  {brandSet && (
                    <SettingFields
                      def={def}
                      form={form}
                      errors={rowErrors}
                      onChange={(key, value) => setValue(def, key, value)}
                    />
                  )}
                </SettingRow>
              );
            }),
            'car settings',
          )}
          <CardFooter>
            <Button
              theme="filled"
              text="Save car settings"
              onClick={saveCarSettings}
              isDisabled={!settingsDirty || !settingsValid || saving}
              isLoading={saving}
            />
          </CardFooter>
        </Card>
      </SettingsSection>

      <SettingsSection id={bookingRulesId} label="Booking rules">
        <Card>
          <CardHeader title="Booking rules" description={`Rules for every trip on ${brandName}.`} />
          {body(
            <>
              <FormGrid $cols={2}>
                <Field
                  label="Minimum driver age"
                  htmlFor="rule-minimum-age"
                  hint={
                    form.ageLimit.mode === 'brand'
                      ? 'Not used: the age limit in Car settings is set by you.'
                      : 'Hosts can ask for an older age on a car, not a younger one. Blank means 18.'
                  }
                  error={rulesResult.errors.minimumAge}
                >
                  <Input
                    id="rule-minimum-age"
                    inputMode="numeric"
                    value={rulesForm.minimumAge}
                    placeholder="18"
                    onChange={e => setRulesForm(f => ({ ...f, minimumAge: e.target.value }))}
                    data-testid="rule-minimum-age"
                  />
                </Field>
                <div />
                <Field
                  label="Security deposit"
                  htmlFor="rule-deposit-collection"
                  hint="Charging a credit card and refunding it later can take 5 to 10 business days to reach the guest."
                >
                  <Select
                    id="rule-deposit-collection"
                    value={rulesForm.depositCollection}
                    onChange={e =>
                      setRulesForm(f => ({
                        ...f,
                        depositCollection: e.target.value as BookingRulesForm['depositCollection'],
                      }))
                    }
                  >
                    <option value="authorize">Hold on credit cards, charge debit cards</option>
                    <option value="charge">Charge every card, refund after the trip</option>
                  </Select>
                </Field>
                <Field
                  label="Release the deposit after (business days)"
                  htmlFor="rule-deposit-release"
                  hint="Blank means 80 hours after the trip ends."
                  error={rulesResult.errors.depositReleaseBusinessDays}
                >
                  <Input
                    id="rule-deposit-release"
                    inputMode="numeric"
                    value={rulesForm.depositReleaseBusinessDays}
                    placeholder="80 hours"
                    onChange={e =>
                      setRulesForm(f => ({ ...f, depositReleaseBusinessDays: e.target.value }))
                    }
                  />
                </Field>
                <Field
                  label="Car card price"
                  htmlFor="rule-car-card-price"
                  hint="The price guests see first on car cards and the home tab selected first. The week price uses each car's daily price and one-week discount. The monthly price is the host's 30-day price, and the Monthly tab lists only cars rented by the month. Monthly sections always show the monthly price; checkout prices are not affected."
                >
                  <Select
                    id="rule-car-card-price"
                    value={rulesForm.carCardPrice}
                    onChange={e =>
                      setRulesForm(f => ({
                        ...f,
                        carCardPrice: e.target.value as BookingRulesForm['carCardPrice'],
                      }))
                    }
                    data-testid="rule-car-card-price"
                  >
                    <option value="daily">Daily price ($50/day)</option>
                    <option value="weekly">Weekly price ($298/wk)</option>
                    <option value="monthly">Monthly price ($1,500/mo)</option>
                  </Select>
                </Field>
              </FormGrid>
              <ToggleRow>
                <div>
                  <SettingTitle>One daily rate</SettingTitle>
                  <SettingHint>
                    Hosts set one price for every day: no weekend, holiday or per-day prices.
                  </SettingHint>
                </div>
                <SwitchComponent
                  isActive={rulesForm.uniformDailyRate}
                  onChange={e => setRulesForm(f => ({ ...f, uniformDailyRate: e.target.checked }))}
                  inputTestId="rule-uniform-daily-rate"
                  ariaLabel="One daily rate"
                />
              </ToggleRow>
              <ToggleRow>
                <div>
                  <SettingTitle>Require a recall check before a car can be booked</SettingTitle>
                  <SettingHint>
                    Guests on this site cannot find or book a car until a recall check is on record.
                    New cars are checked when they are added; others wait in Unlisted cars → Pending
                    recall check.
                  </SettingHint>
                </div>
                <SwitchComponent
                  isActive={rulesForm.requireRecallCheck}
                  onChange={e =>
                    setRulesForm(f => ({ ...f, requireRecallCheck: e.target.checked }))
                  }
                  inputTestId="rule-require-recall-check"
                  ariaLabel="Require a recall check before a car can be booked"
                />
              </ToggleRow>
              <ToggleRow>
                <div>
                  <SettingTitle>
                    Require the name on the card to match the guest's name
                  </SettingTitle>
                  <SettingHint>
                    When off, cards in another name are accepted. The host and brand admins get an
                    email and a notice, listed in Card name checks.
                  </SettingHint>
                </div>
                <SwitchComponent
                  isActive={rulesForm.requireCardholderNameMatch}
                  onChange={e =>
                    setRulesForm(f => ({ ...f, requireCardholderNameMatch: e.target.checked }))
                  }
                  inputTestId="rule-require-cardholder-name-match"
                  ariaLabel="Require the name on the card to match the guest's name"
                />
              </ToggleRow>
            </>,
            'booking rules',
          )}
          <CardFooter>
            <Button
              theme="filled"
              text="Save booking rules"
              onClick={() => save(rulesResult.patch, 'booking rules')}
              isDisabled={!rulesDirty || Object.keys(rulesResult.errors).length > 0 || saving}
              isLoading={saving}
            />
          </CardFooter>
        </Card>
      </SettingsSection>
    </>
  );
}

export default CarSettingsSection;
